import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CircularProgress, Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { useAddEnvironmentalBadgeMutation, useGetCategoriesQuery, useUpdateEnvironmentalBadgeMutation } from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  selectedItem?: any;
}

export default function AddEnvironmentalBadge({
  open,
  onClose,
  setOpen,
  selectedItem
}: ModalProps) {

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<any>({});

  const { data: categoriesData } = useGetCategoriesQuery(
    { search: "", page: 1, limit: 100, type: selectedVehicleType },
    { skip: !selectedVehicleType }
  );
  const [addEnvironmentalBadge, { isLoading: isAdding }] = useAddEnvironmentalBadgeMutation();
  const [updateEnvironmentalBadge, { isLoading: isUpdating }] = useUpdateEnvironmentalBadgeMutation();

  useEffect(() => {
    if (selectedItem) {
      setName(selectedItem.name);
      setName(selectedItem.name);
      setSelectedCategories(selectedItem.category?.map((c: any) => c._id) || []);
      const vType = selectedItem.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setErrors({});
    } else {
      setName("");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setErrors({});
    }
  }, [selectedItem, open]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    const categories = categoriesData?.data?.data || [];

    if (value.includes("all")) {
      if (selectedCategories.length === categories.length) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(categories.map((cat: any) => cat._id));
      }
    } else {
      setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    }
    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    setSelectedCategories([]); // Reset categories when vehicle type changes
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const handleNameChange = (e: any) => {
    const value = e.target.value;
    if (value.length <= 40) {
      setName(value);
      if (errors.name) setErrors({ ...errors, name: "" });
    }
  };

  const handleValidation = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Environmental Badge is required / الشارة البيئية مطلوبة";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required / مطلوب فئة واحدة على الأقل";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required / نوع المركبة مطلوب";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;

    try {
      const payload = {
        name,
        categoryId: selectedCategories,
        type: Number(selectedVehicleType)
      };

      if (selectedItem) {
        await updateEnvironmentalBadge({ id: selectedItem._id, ...payload }).unwrap();
        toast.success("Environmental Badge updated successfully / تم تحديث الشارة البيئية بنجاح");
      } else {
        await addEnvironmentalBadge(payload).unwrap();
        toast.success("Environmental Badge added successfully / تمت إضافة الشارة البيئية بنجاح");
      }
      setOpen(false);
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || "Something went wrong / حدث خطأ ما");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{selectedItem ? "Edit Environmental Badge / تعديل الشارة البيئية" : "Add Environmental Badge / إضافة شارة بيئية"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close / إغلاق"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="control_group">
              <label htmlFor="environmental-badge-name">Environmental Badge / الشارة البيئية</label>
              <TextField
                id="environmental-badge-name"
                placeholder="Enter Badge (e.g., Green Badge) / أدخل الشارة (مثال: الشارة الخضراء)"
                fullWidth
                hiddenLabel
                value={name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group">
              <label htmlFor="VehicleType">Vehicle Type / نوع المركبة</label>
              <Select
                fullWidth
                hiddenLabel
                displayEmpty
                value={selectedVehicleType}
                onChange={handleVehicleTypeChange}
                renderValue={(selected) => {
                  if (!selected) return "Select Vehicle Type / اختر نوع المركبة";
                  return getVehicleTypeLabel(selected);
                }}
                error={!!errors.vehicleType}
              >
                <MenuItem value="" disabled>Select Vehicle Type / اختر نوع المركبة</MenuItem>
                {Object.keys(VEHICLE_TYPE)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                      {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                    </MenuItem>
                  ))}
              </Select>
              {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
            </div>

            <div className="control_group">
              <label htmlFor="Category">Category / الفئة</label>
              <Select
                multiple
                fullWidth
                hiddenLabel
                value={selectedCategories}
                onChange={handleCategoryChange}
                renderValue={(selected) => {
                  const selectedIds = (selected as string[]) || [];
                  if (selectedIds.length === 0) return selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً";

                  return selectedIds.map(id => {
                    const cat = categoriesData?.data?.data?.find((c: any) => c._id === id);
                    if (cat?.name) return cat.name;

                    // Fallback for edit mode if category not in current page
                    const source = selectedItem?.category;
                    const items = Array.isArray(source) ? source : [source];
                    const found = items.find((c: any) => c && typeof c === 'object' && (c._id === id || c.id === id));
                    if (found?.name) return found.name;

                    return id;
                  }).join(", ");
                }}
                disabled={!selectedVehicleType}
                error={!!errors.category}
              >
                <MenuItem value="" disabled>
                  {selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً"}
                </MenuItem>
                {categoriesData?.data?.data?.length > 0 && (
                  <MenuItem value="all">
                    <em>{selectedCategories.length === categoriesData.data.data.length ? "Deselect All / إلغاء تحديد الكل" : "Select All / تحديد الكل"}</em>
                  </MenuItem>
                )}
                {categoriesData?.data?.data?.map((category: any) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Checkbox checked={selectedCategories.includes(category._id)} />
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
            </div>

            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save / حفظ"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
